import { AxiosResponse } from 'axios';

/**
 * The admin API wraps errors as
 * `{ error: true, code, status, message }` (see `HttpResponse.error` on the
 * backend), so `message` carries the readable text. The other keys are checked
 * as a fallback so an unexpected shape still produces something legible rather
 * than "[object Object]".
 */
export const readApiErrorMessage = (response: AxiosResponse | null, fallback: string): string => {
  const payload = response?.data;

  if (!payload) {
    return fallback;
  }

  if (typeof payload === 'string') {
    return payload;
  }

  const { message, body, detail, errors } = payload as Record<string, unknown>;

  if (typeof message === 'string' && message) {
    return message;
  }

  if (typeof body === 'string' && body) {
    return body;
  }

  if (typeof detail === 'string' && detail) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const parts = detail
      .map(entry => {
        if (typeof entry === 'string') {
          return entry;
        }
        const item = entry as { loc?: unknown[]; msg?: string };
        const field = Array.isArray(item.loc)
          ? item.loc.filter(segment => segment !== 'body').join('.')
          : '';
        return field ? `${field}: ${item.msg ?? ''}`.trim() : item.msg ?? '';
      })
      .filter(Boolean);

    if (parts.length) {
      return parts.join('; ');
    }
  }

  if (typeof errors === 'string' && errors) {
    return errors;
  }

  return fallback;
};

export default readApiErrorMessage;
